"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { FileText, Share, Settings } from "lucide-react";
import { useConnectionSignatures, EmailSignature } from "@/lib/hooks/api/emails/email-signatures";
import { ShareSignatureModal } from "./ShareSignatureModal";

interface SignatureSelectorProps {
  connectionId?: string; // The selected email connection
  value?: string;
  onValueChange: (signatureId: string) => void;
  onSignatureContent?: (content: string) => void;
  disabled?: boolean;
  className?: string;
  /** When true, expand inline (search + list below) instead of popover. Use in modals. */
  expandInline?: boolean;
}

export function SignatureSelector({
  connectionId,
  value,
  onValueChange,
  onSignatureContent,
  disabled = false,
  className,
  expandInline = false,
}: SignatureSelectorProps) {
  const router = useRouter();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedSignatureForSharing, setSelectedSignatureForSharing] =
    useState<EmailSignature | null>(null);

  const { data: signatures = [], isLoading } = useConnectionSignatures(connectionId ?? null);

  const handleSignatureSelect = (signatureId: string) => {
    // Find the signature and validate it
    const signature = signatures.find((s) => s.id === signatureId);
    if (!signature) {
      console.error("Selected signature not found");
      return;
    }

    // Basic validation
    if (!signature.content || signature.content.trim().length === 0) {
      console.error("Selected signature has no content");
      return;
    }

    onValueChange(signatureId);

    if (onSignatureContent) {
      onSignatureContent(signature.content);
    }
  };

  const handleShare = (signature: EmailSignature) => {
    setSelectedSignatureForSharing(signature);
    setShareModalOpen(true);
  };

  // Show helpful message when no connection is selected
  if (!connectionId) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ResponsiveCommandSelect
              value=""
              onValueChange={() => {}}
              options={[]}
              placeholder="Select an email account first to choose signatures"
              title="Email Signature"
              description="Email signatures are tied to specific email connections"
              disabled={true}
            />
          </div>
        </div>
      </div>
    );
  }

  const signatureOptions = signatures.map((signature) => ({
    value: signature.id,
    label: signature.name,
    description: signature.is_shared
      ? `Shared by ${signature.owner_name}`
      : signature.is_default
        ? `${signature.name} (Default)`
        : signature.name,
    icon: signature.is_default ? "⭐" : signature.is_shared ? "👥" : "📝",
  }));

  const selectedSignature = signatures.find((s) => s.id === value);

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ResponsiveCommandSelect
            value={value || ""}
            onValueChange={handleSignatureSelect}
            options={signatureOptions}
            placeholder={
              isLoading
                ? "Loading signatures..."
                : signatures.length === 0
                  ? "No signatures for this connection"
                  : "Select signature (optional)"
            }
            title="Email Signature"
            description="Choose a signature to append to your email"
            disabled={disabled || isLoading}
            expandInline={expandInline}
          />
        </div>

        {selectedSignature && !selectedSignature.is_shared && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleShare(selectedSignature)}
            disabled={disabled}
            className="h-9 px-2"
            title="Share this signature"
          >
            <Share className="h-4 w-4" />
          </Button>
        )}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => router.push("/crm/signatures")}
          disabled={disabled}
          className="h-9 px-2"
          title="Manage signatures"
        >
          <Settings className="h-4 w-4" />
        </Button>
      </div>

      {/* Preview of selected signature */}
      {selectedSignature && (
        <div className="mt-2 p-2 bg-muted/30 rounded border text-xs">
          <div className="font-medium text-muted-foreground mb-1">Signature Preview:</div>
          <div
            className="max-h-20 overflow-hidden text-ellipsis"
            dangerouslySetInnerHTML={{ __html: selectedSignature.content }}
          />
        </div>
      )}

      {/* Share Modal */}
      <ShareSignatureModal
        signature={selectedSignatureForSharing}
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setSelectedSignatureForSharing(null);
        }}
      />
    </div>
  );
}
